/** Single source of truth for GSC coverage / indexing status explanations.
 * Call `getCoverageGuide(coverageState)` from any UI — do not duplicate copy elsewhere.
 */

export interface CoverageGuide {
  /** Exact-ish English status as shown by Google */
  matchKey: string;
  title: string;
  paragraphs: string[];
}

const GUIDES: CoverageGuide[] = [
  {
    matchKey: 'submitted and indexed',
    title: 'Submitted and indexed (ارسال‌شده و ایندکس‌شده)',
    paragraphs: [
      'گوگل این آدرس را می‌شناسد و آن را در ایندکس خود ثبت کرده است؛ یعنی صفحه واجد شرایط نمایش در نتایج جستجو است.',
      'معمولاً یعنی صفحه از مسیرهایی مثل نقشه سایت یا درخواست ایندکس به گوگل رسیده و بدون مشکل پوشش ایندکس شده است.',
      'این وضعیت مطلوب است و به‌تنهایی نیاز به رفع خطا ندارد؛ همچنان کیفیت محتوا، لینک‌های داخلی و به‌روز بودن صفحه مهم‌اند.',
      'اگر اخیراً محتوا را عوض کرده‌اید ولی هنوز نسخهٔ قدیمی را می‌بینید، ممکن است خزش بعدی هنوز انجام نشده باشد.',
      'برای اطمینان می‌توانید lastCrawlTime را با lastmod سایت‌مپ مقایسه کنید یا دوباره بازرسی بگیرید.',
    ],
  },
  {
    matchKey: 'url is unknown to google',
    title: 'URL is unknown to Google (آدرس برای گوگل ناشناخته است)',
    paragraphs: [
      'گوگل هنوز این URL را در ایندکس ندارد و حتی نشانه‌ای از کشف قبلی آن در سیستمش ثبت نکرده است.',
      'معمولاً وقتی پیش می‌آید که صفحه خیلی جدید است، لینک داخلی/خارجی قوی ندارد، یا در sitemap ارسال نشده است.',
      'همچنین اگر آدرس اشتباه، ریدایرکت‌شده، یا فقط برای کاربران لاگین‌شده باشد، ممکن است گوگل آن را نشناسد.',
      'برای شروع: صفحه را در sitemap بگذارید، از صفحات مهم به آن لینک داخلی بدهید، و از ابزار بازرسی/درخواست ایندکس استفاده کنید.',
      'بعد از درخواست ایندکس چند روز صبر کنید و دوباره وضعیت را چک کنید؛ کشف و خزش فوری تضمین نمی‌شود.',
    ],
  },
  {
    matchKey: 'alternate page with proper canonical tag',
    title: 'Alternate page with proper canonical tag (صفحه جایگزین با تگ کانونیکال مناسب)',
    paragraphs: [
      'این وضعیت نشان می‌دهد گوگل نسخه تکراری یا جایگزین این صفحه را پیدا کرده است (مثل نسخه موبایل یا URL دارای پارامتر).',
      'چون در این صفحه با تگ canonical به آدرس اصلی اشاره شده، گوگل تصمیم گرفته خود این آدرس را ایندکس نکند.',
      'این حالت خطای فنی نیست و نشان‌دهنده عملکرد درست تگ کانونیکال برای جلوگیری از ایجاد محتوای تکراری است.',
      'تنها زمانی نیاز به بررسی دارد که گوگل اشتباهاً یک صفحه مستقل را به عنوان صفحه جایگزین شناسایی کرده باشد.',
      'برای اصلاح موارد اشتباه، باید تگ canonical در کد HTML صفحه را بررسی کنید تا مستقیماً به خود همان صفحه اشاره کند.',
    ],
  },
  {
    matchKey: 'crawled - currently not indexed',
    title: 'Crawled - currently not indexed (کرول شده - فعلاً ایندکس نشده)',
    paragraphs: [
      'ربات گوگل وارد صفحه شده و محتوای آن را کاملاً خوانده، اما تصمیم گرفته آن را در نتایج جستجو ثبت نکند.',
      'دلیل اصلی این مسئله معمولاً کیفیت پایین محتوا، تکراری بودن یا عدم ارزیابی صفحه به عنوان یک صفحه ارزشمند است.',
      'همچنین ممکن است به دلیل محدودیت بودجه خزش (Crawl Budget) یا تازه ساخت بودن سایت این اتفاق افتاده باشد.',
      'برای حل آن باید کیفیت محتوا را ارتقا داده، لینک‌سازی داخلی را تقویت کنید و ارزش افزوده به صفحه ببخشید.',
      'این وضعیت نیاز به رفع خطای کدنویسی ندارد، بلکه نیازمند بهبود سئوی محتوایی و اعتبار صفحه است.',
    ],
  },
  {
    matchKey: 'discovered - currently not indexed',
    title: 'Discovered - currently not indexed (شناسایی شده - فعلاً ایندکس نشده)',
    paragraphs: [
      'گوگل از وجود این آدرس (مثلاً از طریق نقشه سایت یا لینک‌های داخلی) باخبر شده، اما هنوز آن را کرول نکرده است.',
      'علت اصلی آن می‌تواند ترافیک بالای سرور، سرعت پایین سایت یا عدم اولویت‌بندی این آدرس توسط ربات‌های گوگل باشد.',
      'اگر سرور شما در زمان مراجعه گوگل کند باشد، گوگل فرآیند خزش صفحات جدید را به تأخیر می‌اندازد.',
      'برای رفع این مشکل، سرعت سرور را بهینه‌سازی کرده و معماری لینک‌سازی داخلی سایت را ساده‌تر کنید.',
      'معمولاً با گذشت زمان و افزایش اعتبار کلی سایت، این صفحات به‌مرور زمان کرول و ایندکس می‌شوند.',
    ],
  },
  {
    matchKey: 'duplicate without user-selected canonical',
    title: 'Duplicate without user-selected canonical (تکراری بدون canonical انتخابی)',
    paragraphs: [
      'گوگل این URL را تکراری می‌داند و canonical مشخصی از سمت شما انتخاب نشده است.',
      'معمولاً باید بررسی کنید کدام نسخه باید ایندکس شود و تگ canonical را درست تنظیم کنید.',
      'این وضعیت در bucket ایندکس‌نشده (excluded) قرار می‌گیرد؛ bucket یعنی دستهٔ خلاصهٔ وضعیت ایندکس (ایندکس‌شده / ایندکس‌نیست / خطا).',
    ],
  },
  {
    matchKey: "excluded by 'noindex' tag",
    title: "Excluded by 'noindex' tag (استثنا شده توسط تگ noindex)",
    paragraphs: [
      'این پیام یعنی گوگل قصد ایندکس صفحه را داشته، اما با دستور noindex در کدهای HTML یا هدر صفحه مواجه شده است.',
      'اگر این صفحه نباید در نتایج گوگل نشان داده شود (مثل صفحه سبد خرید یا پنل کاربری)، این وضعیت کاملاً درست است.',
      'اما اگر صفحه‌ای مهم (مثل مقالات یا محصولات) این پیغام را می‌دهد، نشان‌دهنده یک اشتباه فنی در تنظیمات سایت است.',
      'برای حل آن باید تگ meta noindex را از بخش head کد صفحه یا تنظیمات افزونه سئو حذف کنید.',
      'پس از حذف این تگ، می‌توانید از طریق ابزار URL Inspector درخواست بررسی مجدد (Request Indexing) ثبت کنید.',
    ],
  },
  {
    matchKey: 'soft 404',
    title: 'Soft 404 (خطای ۴۰۴ نرم)',
    paragraphs: [
      'این خطا زمانی رخ می‌دهد که صفحه از نظر کد فنی وضعیت 200 (موفق) برمی‌گرداند، اما محتوای آن خالی یا شبیه ۴۰۴ است.',
      'گوگل این صفحات را گمراه‌کننده می‌داند چون به کاربر محتوایی ارائه نمی‌دهد اما به ربات می‌گوید صفحه وجود دارد.',
      'همچنین ریدایرکت کردن صفحات حذف‌شده به صفحه اصلی سایت (Homepage) یکی از دلایل اصلی بروز این خطا است.',
      'برای رفع آن، اگر صفحه وجود ندارد کد وضعیت آن را به ۴۰۴ یا ۴۱۰ واقعی تغییر دهید.',
      'اگر صفحه واقعی است، محتوای متنی مرتبط و کافی به آن اضافه کنید تا گوگل آن را صفحه خالی تشخیص ندهد.',
    ],
  },
  {
    matchKey: 'server error (5xx)',
    title: 'Server error (5xx) (خطای سرور ۵xx)',
    paragraphs: [
      'این پیام نشان می‌دهد هنگام تلاش ربات گوگل برای بازدید از صفحه، سرور شما دچار قطعی یا اختلال شده است.',
      'خطاهایی مثل 500 (خطای داخلی سرور) یا 503 (عدم دسترس‌پذیری سرویس) زیرمجموعه این بخش هستند.',
      'این خطا مانع ایندکس شدن صفحه می‌شود و در صورت تداوم، رتبه صفحات موجود سایت را نیز به‌شدت کاهش می‌دهد.',
      'برای حل مشکل باید لاگ‌های سرور (Server Logs) و میزان مصرف منابع هاست را بررسی کرده یا با پشتیبان هاست تماس بگیرید.',
      'تداخل افزونه‌ها، کدهای مخرب یا فشار بیش از حد به دیتابیس از عوامل شایع بروز این خطای سرور هستند.',
    ],
  },
  {
    matchKey: 'page with redirect',
    title: 'Page with redirect (صفحه دارای ریدایرکت)',
    paragraphs: [
      'گوگل این آدرس را باز کرده اما بلافاصله به آدرس دیگری (مثلاً ریدایرکت ۳۰۱ یا ۳۰۲) هدایت (Redirect) شده است.',
      'گوگل آدرس مبدأ را ایندکس نمی‌کند و به‌جای آن سعی می‌کند آدرس مقصد را بررسی و در نتایج ثبت کند.',
      'این وضعیت برای آدرس‌های تغییریافته طبیعی است، اما وجود زنجیره ریدایرکت (Redirect Chain) مشکل‌ساز خواهد بود.',
      'برای بهینه‌سازی، لینک‌های داخلی سایت را ویرایش کنید تا مستقیماً به آدرس نهایی و جدید اشاره کنند.',
      'مطمئن شوید آدرس مقصد بدون مشکل باز می‌شود و خودش به آدرس دیگری ریدایرکت نشده باشد.',
    ],
  },
  {
    matchKey: 'blocked by robots.txt',
    title: 'Blocked by robots.txt (بلاک شده توسط فایل robots.txt)',
    paragraphs: [
      'فایل robots.txt سایت شما حاوی دستوری (Disallow) است که مانع دسترسی ربات گوگل به این URL خاص می‌شود.',
      'اگر قصد داشته‌اید دسترسی گوگل به این بخش را ببندید، این وضعیت طبیعی است و نیازی به تغییر ندارد.',
      'اما اگر این صفحه باید در نتایج گوگل باشد، باید خط مربوطه را در فایل robots.txt اصلاح یا حذف کنید.',
      'توجه داشته باشید که گاهی صفحات بلاک‌شده با robots.txt ممکن است همچنان بدون محتوا در گوگل ایندکس شوند.',
      'برای تست دقیق دسترسی، می‌توانید دستورات موجود در فایل robots.txt را در ابزار بررسی سرچ کنسول تست کنید.',
    ],
  },
];

function normalizeCoverageKey(value: string): string {
  return value
    .toLowerCase()
    .replace(/[\u2018\u2019\u201A\u201B\u2039\u203A]/g, "'")
    .replace(/[\u201C\u201D\u201E\u00AB\u00BB]/g, '"')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Returns a user-facing guide when the GSC coverage/indexing status is one we explain. */
export function getCoverageGuide(
  coverageState?: string | null,
): CoverageGuide | null {
  if (!coverageState?.trim()) return null;
  const normalized = normalizeCoverageKey(coverageState);
  for (const guide of GUIDES) {
    if (normalized.includes(guide.matchKey)) return guide;
  }
  // Soft 404 / 5xx sometimes appear without the exact GSC wording.
  if (normalized.includes('server error')) {
    return GUIDES.find((g) => g.matchKey === 'server error (5xx)') ?? null;
  }
  return null;
}
